import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { TPAReportTab } from "../components/reports/TPAReportTab";
import { formatCurrency, formatAxisCurrency, getCurrencySymbol, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  RefreshCw,
  BarChart3,
  PieChart,
  LineChart as LineChartIcon,
  DollarSign,
  ShoppingCart,
  Users,
  Package,
  CreditCard,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const ReportsPage = () => {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7");
  const [salesData, setSalesData] = useState([]);
  const [stats, setStats] = useState(null);
  const [productStats, setProductStats] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchReports();
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [salesRes, statsRes, productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/sales-chart?days=${period}`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/dashboard/stats`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/categories`, { headers, withCredentials: true }),
      ]);

      setSalesData(salesRes.data);
      setStats(statsRes.data);
      setProductStats(productsRes.data);
      setCategoryStats(categoriesRes.data);
    } catch (error) {
      toast.error("Erro ao carregar relatórios");
    } finally {
      setLoading(false);
    }
  };

  const totalSales = salesData.reduce((sum, day) => sum + day.sales, 0);
  const totalOrders = salesData.reduce((sum, day) => sum + day.orders, 0);
  const avgDaily = salesData.length > 0 ? totalSales / salesData.length : 0;

  // Prepare category chart data
  const categoryChartData = categoryStats.map((cat, index) => {
    const productsInCategory = productStats.filter((p) => p.category_id === cat.category_id);
    return {
      name: cat.name,
      products: productsInCategory.length,
      color: ["#F97316", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899"][index % 5],
    };
  });

  // Orders by type mock data (would come from backend)
  const orderTypeData = [
    { name: "Mesa", value: 60, color: "#F97316" },
    { name: "Take-away", value: 30, color: "#10B981" },
    { name: "Delivery", value: 10, color: "#3B82F6" },
  ];

  // Payment methods mock data
  const paymentData = [
    { name: "Dinheiro", value: 45, color: "#10B981" },
    { name: "Cartão", value: 55, color: "#3B82F6" },
  ];

  const exportExcel = async (report) => {
    try {
      const end = new Date().toISOString().slice(0, 10);
      const startD = new Date(Date.now() - parseInt(period) * 86400000).toISOString().slice(0, 10);
      const res = await axios.get(`${API_URL}/reports/export/excel?report=${report}&start=${startD}&end=${end}`, {
        headers, withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `relatorio_${report}_${startD}_${end}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Relatório Excel exportado");
    } catch { toast.error("Erro ao exportar relatório"); }
  };

  const exportToCSV = () => {
    const headers = ["Data", "Vendas", "Pedidos"];
    const rows = salesData.map((d) => [d.date, d.sales.toFixed(2), d.orders]);
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio-vendas-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Relatório exportado!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">{t("reports")}</h1>
          <p className="text-muted-foreground">Análises e estatísticas do negócio</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="14">Últimos 14 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchReports}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-2" data-testid="export-excel-btn">
                <Download className="w-4 h-4" />
                Exportar Excel
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportExcel("sales")} data-testid="export-sales">Vendas por Período</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportExcel("products")} data-testid="export-products">Produtos & Lucro</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportExcel("employees")} data-testid="export-employees">Performance Funcionários</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Período</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(totalSales)}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <DollarSign className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-emerald-500">
              <TrendingUp className="w-4 h-4" />
              <span>+12.5% vs período anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Pedidos</p>
                <p className="text-2xl font-bold">{totalOrders}</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10">
                <ShoppingCart className="w-6 h-6 text-blue-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-blue-500">
              <TrendingUp className="w-4 h-4" />
              <span>+8 vs período anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Média Diária</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(avgDaily)}</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/10">
                <BarChart3 className="w-6 h-6 text-amber-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
              <span>Baseado em {period} dias</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ticket Médio</p>
                <p className="text-2xl font-bold font-mono">
                  {formatCurrency(totalOrders > 0 ? totalSales / totalOrders : 0)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/10">
                <Users className="w-6 h-6 text-purple-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-emerald-500">
              <TrendingUp className="w-4 h-4" />
              <span>+2.3% vs período anterior</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="sales" className="space-y-6">
        <TabsList>
          <TabsTrigger value="sales" className="gap-2">
            <LineChartIcon className="w-4 h-4" />
            Vendas
          </TabsTrigger>
          <TabsTrigger value="products" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Produtos
          </TabsTrigger>
          <TabsTrigger value="distribution" className="gap-2">
            <PieChart className="w-4 h-4" />
            Distribuição
          </TabsTrigger>
          <TabsTrigger value="tpa" className="gap-2" data-testid="reports-tab-tpa">
            <CreditCard className="w-4 h-4" />
            TPA / Bancos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tpa">
          <TPAReportTab headers={headers} />
        </TabsContent>

        {/* Sales Chart */}
        <TabsContent value="sales">
          <Card>
            <CardHeader>
              <CardTitle className="font-['Outfit']">Evolução de Vendas</CardTitle>
              <CardDescription>Vendas e pedidos por dia</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => value.slice(5)}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <YAxis
                      yAxisId="left"
                      stroke="hsl(var(--muted-foreground))"
                      tickFormatter={(value) => formatAxisCurrency(value)}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="sales"
                      name={`Vendas (${getCurrencySymbol()})`}
                      stroke="#F97316"
                      strokeWidth={3}
                      dot={{ fill: "#F97316" }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="orders"
                      name="Pedidos"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={{ fill: "#3B82F6" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products Chart */}
        <TabsContent value="products">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Outfit']">Produtos Mais Vendidos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats?.top_products || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={100}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Bar dataKey="quantity" name="Quantidade" fill="#F97316" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-['Outfit']">Receita por Produto</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats?.top_products || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        type="number"
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => formatAxisCurrency(value)}
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={100}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                        formatter={(value) => [formatCurrency(value), "Receita"]}
                      />
                      <Bar dataKey="revenue" name="Receita" fill="#10B981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Distribution Charts */}
        <TabsContent value="distribution">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Outfit']">Por Tipo de Pedido</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={orderTypeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {orderTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-['Outfit']">Por Forma de Pagamento</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={paymentData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {paymentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-['Outfit']">Por Categoria</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={categoryChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="products"
                      >
                        {categoryChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
